import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { Icon, type IconName } from './icon';

export type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  label: string;
  /** Icône affichée à gauche, dans le cadre. */
  icon?: IconName;
  className?: string;
  /** Message d'erreur : passe le cadre en rouge et l'annonce au lecteur d'écran. */
  error?: string;
};

/**
 * Champ libellé + cadre. Le focus est géré en CSS (`focus-within`) et non en JS
 * comme dans la maquette, pour que le champ fonctionne côté serveur.
 */
export function Field({ label, icon, className, error, id, ...input }: FieldProps) {
  const inputId = id ?? `field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const errorId = `${inputId}-error`;
  return (
    <label className={cn('block', className)} htmlFor={inputId}>
      <span className="mb-[7px] block text-[13.5px] font-semibold text-ink">{label}</span>
      <div
        className={cn(
          'flex items-center gap-2.5 rounded-btn border-[1.5px] bg-white px-[14px] transition-colors',
          error ? 'border-red' : 'border-line focus-within:border-green',
        )}
      >
        {icon ? (
          <span className="flex text-mut">
            <Icon name={icon} size={18} />
          </span>
        ) : null}
        <input
          id={inputId}
          className="flex-1 border-none bg-transparent py-[13px] font-sans text-[15px] text-ink outline-none"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...input}
        />
      </div>
      {error ? (
        <span id={errorId} className="mt-1.5 block text-[13px] font-medium text-red">
          {error}
        </span>
      ) : null}
    </label>
  );
}
